'use client';

// Shared subscription / site-ownership logic used by both the nag banner
// (app/components/SubscriptionBanner.tsx) and the hard lockout gate
// (app/components/SubscriptionGate.tsx). Keeping the Chargebee status
// vocabulary, the exempt-installer list, and the `/api/sites` ownership
// lookup in one place means the two features can never drift apart on who
// counts as a paying owner.

import { useEffect, useState } from 'react';
import { useOptionalUser } from '@/context/UserContext';
import {
  SUBSCRIPTION_EXEMPT_INSTALLER_IDS,
  classifySubscriptionSites,
  isActiveLikeStatus,
  isTerminatedStatus,
} from '@/app/lib/subscriptionStatus';

// Re-export the pure status helpers so existing consumers can keep importing
// everything subscription-related from this module.
export {
  SUBSCRIPTION_EXEMPT_INSTALLER_IDS,
  isActiveLikeStatus,
  isTerminatedStatus,
};

export type OwnershipState = {
  ownsSite: boolean;
  exemptByInstaller: boolean;
};

// Module-level cache of the "does this user own any sites" answer so we don't
// re-fetch /api/sites on every navigation. Keyed by user id because in dev
// you can swap accounts without a full reload.
const ownsSitesCache: Map<number, OwnershipState | Promise<OwnershipState>> = new Map();

export async function fetchOwnership(
  userId: number,
  userEmail?: string | null,
): Promise<OwnershipState> {
  const cached = ownsSitesCache.get(userId);
  if (cached && !(cached instanceof Promise)) return cached;
  if (cached instanceof Promise) return cached;
  const p = (async () => {
    try {
      const res = await fetch('/api/sites', { credentials: 'include' });
      if (!res.ok) return { ownsSite: false, exemptByInstaller: false }; // fail-open
      const data = await res.json();
      const sites = Array.isArray(data) ? data : (Array.isArray(data?.sites) ? data.sites : []);
      const result = classifySubscriptionSites(userId, userEmail, sites);
      ownsSitesCache.set(userId, result);
      return result;
    } catch {
      return { ownsSite: false, exemptByInstaller: false };
    }
  })();
  ownsSitesCache.set(userId, p);
  return p;
}

// Resolve whether the current user owns a (non-exempt) site. Returns `null`
// until known. Pass `enabled = false` to skip the fetch entirely for the
// majority of users (staff, active subscribers) who can never be locked out —
// they never pay the /api/sites round-trip.
export function useSiteOwnership(enabled: boolean): OwnershipState | null {
  const ctx = useOptionalUser();
  const userId = ctx?.user?.id;
  const userEmail = ctx?.user?.email;
  const [ownership, setOwnership] = useState<OwnershipState | null>(null);

  useEffect(() => {
    if (!enabled || !userId) {
      setOwnership(null);
      return;
    }
    let cancelled = false;
    fetchOwnership(userId, userEmail).then(result => {
      if (!cancelled) setOwnership(result);
    });
    return () => { cancelled = true; };
  }, [enabled, userId, userEmail]);

  return enabled ? ownership : null;
}

export type SubscriptionAccessState = 'open' | 'locked';

// The single source of truth for "should this user be locked out of the
// customer data pages". The lockout is driven purely by the user's own
// subscription status — no site-ownership or bundled-installer exemptions:
//   - signed in, and
//   - not staff (admins / installers manage customer accounts and don't hold
//     a personal customer subscription), and
//   - their own subscription status is terminated (cancelled / transferred).
// This is deliberately stricter than the nag banner, which keeps the softer
// ownership / bundled-installer (37/50) exemptions — a cancelled subscription
// is a definitive per-user signal, so we act on it directly and synchronously
// (no /api/sites round-trip, so no flash of data we're about to hide).
export function useSubscriptionAccess(): SubscriptionAccessState {
  const ctx = useOptionalUser();
  const user = ctx?.user;
  if (!user) return 'open';
  if (ctx?.isAdmin || ctx?.isInstaller) return 'open';
  return isTerminatedStatus(user.subscription_status) ? 'locked' : 'open';
}
